/**
 * The seam between this app's row shapes and rdx-platform-api's V3 visitor
 * model (2flu.9, 2 Oct 2026).
 *
 * The screens were built against an API branch that never merged, where a
 * visit carried `visitorTypeId` / `visitorType`, a `location { name }` and a
 * legacy questionnaire, and a person carried a type and `accessNeeds`. The
 * platform has none of those: a visit names its `reasonId` (Settings calls
 * reasons "visitor types"), a site is a `locationId`, and a person's
 * accessibility needs are `accessibilityNeeds`, captured by the visitor
 * themselves. The UI keeps its row shapes (`ApiVisit`, `ApiVisitor`); this file
 * adapts in both directions, so a wire change lands here and nowhere else.
 *
 * Inputs are WHITELISTED, not spread. GraphQL rejects an input object carrying
 * a field its type does not declare ("Field X is not defined by type
 * CreateVisitInput"), and that failure is invisible to document validation —
 * the operation is valid, the variables are not. `platform-mapping.spec.ts`
 * checks every whitelist below against the vendored API schema
 * (`contracts/api-schema.graphql`), so an input field the API drops fails the
 * build here.
 */

import type { ApiVisit, ApiVisitor, ApiVisitorTypeSettings } from './api-types';

/** `CreateVisitInput` fields this app sends. */
export const CREATE_VISIT_INPUT_FIELDS = [
  'locationId',
  'expectedDate',
  'expectedEnd',
  'reasonId',
  'hostUserId',
  'sendInvitation',
  'notes',
  'tags',
  'badgeNumber',
  'visitorId',
  'visitorFirstName',
  'visitorLastName',
  'visitorEmail',
  'visitorCompany',
  'visitorMobile',
  'visitorCountryCode',
  'visitorJobTitle',
  'visitorCarRegistration',
] as const;

/** `CreateVisitorInput` fields this app sends (create and update). */
export const VISITOR_INPUT_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'mobile',
  'countryCode',
  'company',
  'jobTitle',
  'carRegistration',
  'isPrivate',
  'tags',
] as const;

/** `UpdateVisitInput` fields this app sends. */
export const UPDATE_VISIT_INPUT_FIELDS = [
  'expectedDate',
  'expectedEnd',
  'notes',
  'reasonId',
  'badgeNumber',
  'tags',
] as const;

type Plain = Record<string, unknown>;

function pick(source: Plain, fields: readonly string[]): Plain {
  const out: Plain = {};
  for (const field of fields) {
    const value = source[field];
    if (value !== undefined) out[field] = value;
  }
  return out;
}

/** The app's "visitor type" is the platform's reason. */
function withReason(source: Plain): Plain {
  if (source['reasonId'] !== undefined || source['visitorTypeId'] === undefined) {
    return source;
  }
  return { ...source, reasonId: source['visitorTypeId'] || undefined };
}

export function toCreateVisitInput(input: Plain): Plain {
  return pick(withReason(input), CREATE_VISIT_INPUT_FIELDS);
}

export function toUpdateVisitInput(input: Plain): Plain {
  return pick(withReason(input), UPDATE_VISIT_INPUT_FIELDS);
}

export function toVisitorInput(input: Plain): Plain {
  return pick(input, VISITOR_INPUT_FIELDS);
}

/** When an ObjectId was minted — the platform keeps no `createdAt` on a person. */
export function createdAtFromId(id: string | null | undefined): string | null {
  if (!id || !/^[0-9a-f]{24}$/i.test(id)) return null;
  return new Date(parseInt(id.slice(0, 8), 16) * 1000).toISOString();
}

/** A person off the wire, in the shape the address book renders. */
export function adaptVisitor(
  wire: ApiVisitor & { accessibilityNeeds?: string | null },
): ApiVisitor {
  return {
    ...wire,
    accessNeeds: wire.accessibilityNeeds ?? wire.accessNeeds ?? null,
    createdAt: wire.createdAt ?? createdAtFromId(wire._id),
  };
}

export interface VisitContext {
  typeName(id: string | null | undefined): string | null;
  locationName(id: string | null | undefined): string | null;
}

/** A visit off the wire, in the shape the grids and the panel render. */
export function adaptVisit(
  wire: ApiVisit & { reasonId?: string | null },
  context: VisitContext,
): ApiVisit {
  const reasonId = wire.reasonId ?? wire.visitorTypeId ?? null;
  return {
    ...wire,
    visitorTypeId: reasonId,
    visitorType: wire.visitorType ?? context.typeName(reasonId),
    location: wire.locationId
      ? { _id: wire.locationId, name: context.locationName(wire.locationId) }
      : (wire.location ?? null),
    visitor: wire.visitor
      ? adaptVisitor(wire.visitor as ApiVisitor & { accessibilityNeeds?: string | null })
      : wire.visitor,
  };
}

/** A reason's registration fields, as the Add visit form reads type settings. */
export interface ReasonForSettings {
  _id: string;
  name?: string | null;
  isDefault?: boolean | null;
  fieldSet?: { field: string; required?: boolean | null }[] | null;
}

export function toTypeSettings(
  reason: ReasonForSettings,
  locationId: string,
): ApiVisitorTypeSettings {
  const field = (name: string) => {
    const entry = (reason.fieldSet ?? []).find((f) => f.field === name);
    return { enabled: !!entry, required: !!entry?.required };
  };
  return {
    _id: reason._id,
    visitorTypeId: reason._id,
    locationId,
    name: reason.name ?? null,
    enabled: true,
    isDefault: !!reason.isDefault,
    // The platform has no per-type verification switch; requirements and
    // the door decide who is admitted.
    requiresVerification: false,
    visitorFields: {
      carReg: field('carRegistration'),
      company: field('company'),
      mobileNumber: field('mobile'),
      jobTitle: field('jobTitle'),
      email: field('email'),
      // The host is always asked for, and is the visit's, not the reason's.
      visiting: { enabled: true, required: false },
    },
  };
}
